import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Page from 'src/components/Page';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  LoadingTableRow,
} from 'src/components/table';
import useTable, { emptyRows, getComparator } from 'src/hooks/useTable';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { paramCase } from 'change-case';
import FhirOrganizationTableToolbar from '../components/common/FhirOrganizationTableToolbar';
import FhirOrganizationTableRow from '../components/common/FhirOrganizationTableRow';
import FilterDrawer from 'src/components/FilterDrawer';
import useObjectState from 'src/hooks/useObjectState';
import { FilterList } from '@mui/icons-material';
import { getUUID } from 'src/utils/url';
import CreateOrganizationButton from '../components/search/CreateOrganizationButton';
import { useOrganizations, useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  OrganizationWrapper,
  WrappedOrganization,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { OrganizationAclsArray } from 'src/careflow/ph-core/admin/organization/type/organization';
import {
  checkAclValidation,
  getRelatedAcls,
} from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { iconGreyFillColor } from 'src/careflow/helpers/constants';
import { translateTableHead } from 'src/careflow/helpers/common';
import { TABLE_HEAD_ORGANIZATION } from 'src/careflow/common/table-head';
import useTranslations from 'src/careflow/ph-core/internationalization/hook/useTranslations';
import useSettings from 'src/careflow/ph-core/multi-tenant/setting/useSettings';
import { WarningIcon } from 'src/theme/overrides/CustomIcons';
import { useSnackbar } from 'notistack';

export default function FhirOrganizationList() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { enqueueSnackbar } = useSnackbar();

  const [{ openFilterDrawer, showError, openDialog, fhirId, isDeleting }, updateComponentState] =
    useObjectState<{
      openFilterDrawer: boolean;
      showError: string | null;
      openDialog: boolean;
      fhirId: string | null;
      isDeleting: boolean;
    }>({
      openFilterDrawer: false,
      showError: null,
      openDialog: false,
      fhirId: null,
      isDeleting: false,
    });

  const [organizations, { isLoading, remove, refresh }] = useOrganizations({
    map: OrganizationWrapper,
  });

  const [valueSet] = useValueSet('organization-types', { map: ValueSetWrapper });

  const { themeStretch } = useSettings();

  const translate = useTranslations(`fhirOrganization`, 'translations');

  const navigate = useNavigate();

  const [{ name, active, type, filters }, updateFilterState] = useObjectState<{
    name: string | null;
    active: boolean | null;
    type: any;
    filters: any;
  }>({
    name: null,
    active: null,
    type: [],
    filters: null,
  });

  const auth = useAuth();

  const organizationsMaps = useMemo(() => {
    if (!organizations?.length || isLoading) return [];

    const currentUser = auth.getCurrentUser();
    const acls: Partial<OrganizationAclsArray> = {
      all: getRelatedAcls([crsAcls.ADMIN.FHIR_ORGANIZATIONS.ALL]),
      ownOrg: getRelatedAcls([crsAcls.ADMIN.FHIR_ORGANIZATIONS.OWN_ORGANIZATION.VIEW]),
      childOrg: getRelatedAcls([crsAcls.ADMIN.FHIR_ORGANIZATIONS.CHILD_ORGANIZATIONS.VIEW]),
    };

    const getOrganizations = getOrganizationByAcl(currentUser, acls, organizations);

    return getOrganizations;
  }, [organizations]);

  const deleteOrganization = async () => {
    try {
      updateComponentState({ isDeleting: true });
      await remove(fhirId!);
      enqueueSnackbar(translate('dialogs.delete'));
      onClose();
      await refresh();
    } catch (error) {
      updateComponentState({ showError: error?.issue?.[0]?.details?.text ?? 'error' });
    } finally {
      updateComponentState({ isDeleting: false });
    }
  };

  const handleEditRow = (id: string) => {
    navigate(PATH_DASHBOARD.fhirOrganization.details(paramCase(id)));
  };

  const dataFiltered = applySortFilter({
    tableData: organizationsMaps,
    comparator: getComparator(order, orderBy),
    name,
    active: filters?.active ?? true,
    type: filters?.type ?? [],
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound = !dataFiltered.length;

  const onClose = () => {
    updateComponentState({
      showError: null,
      fhirId: null,
      isDeleting: false,
      openDialog: false,
    });
  };

  return (
    <Page title={translate('titleList')}>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          title={translate('titleList')}
          links={[
            { name: `${translate('dashboard')}`, href: PATH_DASHBOARD.root },
            { name: `${translate('titleList')}`, href: PATH_DASHBOARD.fhirOrganization.root },
          ]}
          heading=""
          action={<CreateOrganizationButton translate={translate} refresh={refresh} />}
        />

        <Card sx={{ width: '100%', height: '100%', paddingTop: 3 }}>
          <FhirOrganizationTableToolbar
            searchTextFieldProps={{
              placeholder: translate('list.filter.searchFieldPlaceholder'),
              onChange: (event) => {
                updateFilterState({
                  name: event.target.value,
                });
              },
            }}
            containerSx={{ marginBottom: 3, paddingX: 2.5 }}
            childrenFilter={
              <IconButton onClick={() => updateComponentState({ openFilterDrawer: true })}>
                <FilterList htmlColor={iconGreyFillColor} />
              </IconButton>
            }
          />

          <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
            <Table size={dense ? 'small' : 'medium'}>
              <TableHeadCustom
                order={order}
                orderBy={orderBy}
                headLabel={translateTableHead(TABLE_HEAD_ORGANIZATION, translate, 'translations')}
                rowCount={organizations.length}
                onSort={onSort}
              />

              {!isLoading ? (
                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <FhirOrganizationTableRow
                        key={row.id}
                        row={row}
                        onDeleteRow={() => {
                          updateComponentState({
                            fhirId: row.id,
                            openDialog: true,
                          });
                        }}
                        onEditRow={() => handleEditRow(row.id!)}
                        customLink={true}
                        translate={translate}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(page, rowsPerPage, organizations.length)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              ) : (
                <LoadingTableRow colSpan={TABLE_HEAD_ORGANIZATION.length} />
              )}
            </Table>
          </TableContainer>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={dataFiltered.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
        <FilterDrawer
          title={translate('list.filter.title')}
          onApplyButtonClick={() => {
            updateFilterState((prevGridState: any) => ({
              ...prevGridState,
              filters: {
                active: active,
                type: type,
              },
            }));
            setPage(0);
          }}
          onCloseIconButtonClick={() => updateComponentState({ openFilterDrawer: false })}
          onClearAllButtonClick={() =>
            updateFilterState({
              active: null,
              type: [],
            })
          }
          anchor="right"
          open={openFilterDrawer}
        >
          <Box py={3}>
            <Box>
              <Grid item sx={{ marginTop: 1 }} xs={12}>
                <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
                  {translate('list.filter.type')}
                </Typography>
                <Autocomplete
                  id="combo-box-type"
                  value={type}
                  fullWidth
                  multiple
                  onChange={(_: React.SyntheticEvent, value: any) => {
                    updateFilterState((prevGridState: any) => ({
                      ...prevGridState,
                      type: value,
                    }));
                  }}
                  options={valueSet?.asList() ?? []}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : option?.display ?? ''
                  }
                  isOptionEqualToValue={(option, value) =>
                    typeof option !== 'string' &&
                    typeof value !== 'string' &&
                    option.code === value.code
                  }
                  filterOptions={(options, params) =>
                    options?.filter((item) =>
                      item?.display?.toLowerCase().includes(params.inputValue.toLowerCase())
                    )
                  }
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const tagProps = getTagProps({ index });

                      const label = typeof option === 'string' ? option : option?.display ?? '';

                      return <Chip {...tagProps} key={index} size="small" label={label} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('list.filter.type')}
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {!valueSet?.asListAll?.()?.length && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item sx={{ marginTop: 1 }} xs={12}>
                <Typography fontSize={'1rem'} fontWeight={'bold'} marginBottom={1.4}>
                  {translate('list.filter.active')}
                </Typography>
                <Switch
                  name="active"
                  onChange={(_, active) => {
                    updateFilterState((prevGridState: any) => ({
                      ...prevGridState,
                      active: active,
                    }));
                  }}
                  checked={active ?? true}
                />
              </Grid>
            </Box>
          </Box>
        </FilterDrawer>
      </Container>
      <DialogDelete
        open={openDialog}
        handleClose={onClose}
        deleteOrganization={deleteOrganization}
        showError={showError}
        translate={translate}
        isDeleting={isDeleting}
      />
    </Page>
  );
}

export function applySortFilter({
  tableData,
  comparator,
  name,
  active,
  type,
}: {
  tableData: WrappedOrganization[];
  comparator: (a: any, b: any) => number;
  name: string | null;
  active: boolean | null;
  type: any;
}) {
  const stabilizedThis = tableData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (name !== null) {
    tableData = tableData?.filter(
      (item: Record<string, any>) => item.name.toLowerCase().indexOf(name.toLowerCase()) !== -1
    );
  }

  if (active !== null) {
    tableData = tableData?.filter((item: Record<string, any>) => item.active === active);
  }

  if (type?.length > 0) {
    const mapType = type?.map((e) => e?.code?.toLowerCase());
    tableData = tableData?.filter((item: Record<string, any>) =>
      item?.type?.some((e) => mapType?.includes(e?.coding?.[0]?.code.toLowerCase()))
    );
  }

  return tableData;
}

function getOrganizationByAcl(
  currentUser: any,
  acls: Partial<OrganizationAclsArray>,
  organizations: WrappedOrganization[] | null
): WrappedOrganization[] {
  if (!organizations?.length) return [];

  const currentUserOrg = getUUID(currentUser?.organization_fhir_uri);

  const userOrg = organizations.find((o) => String(o.id) === String(currentUserOrg)) ?? null;

  const childOrganizations =
    currentUser?.organizations?.filter((org: WrappedOrganization) => org.id !== userOrg?.id) ?? [];

  if (acls.all && checkAclValidation({ acls: acls.all })) {
    return organizations;
  }

  const result = new Set<WrappedOrganization>();

  if (acls.ownAndChildOrg && checkAclValidation({ acls: acls.ownAndChildOrg })) {
    if (userOrg) result.add(userOrg);
    childOrganizations.forEach((org) => result.add(org));
  }

  if (acls.ownOrg && checkAclValidation({ acls: acls.ownOrg })) {
    if (userOrg) result.add(userOrg);
  }

  if (acls.childOrg && checkAclValidation({ acls: acls.childOrg })) {
    childOrganizations.forEach((org) => result.add(org));
  }

  return Array.from(result);
}

const DialogDelete = ({
  open,
  handleClose,
  showError = null,
  isDeleting = false,
  deleteOrganization,
  translate,
}: {
  open: boolean;
  handleClose: VoidFunction;
  showError: string | null;
  isDeleting: boolean;
  deleteOrganization: Function;
  translate: any;
}) => (
  <Dialog
    open={open}
    onClose={handleClose}
    fullWidth={true}
    maxWidth="sm"
    sx={{
      '& .MuiDialog-paper.MuiDialog-paper .MuiDialogActions-root': {
        p: '0px 24px 24px 24px',
      },
    }}
  >
    <DialogTitle mb={1}>{translate('list.delete')}</DialogTitle>
    <DialogContent>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        {translate('dialogs.deleteConfirmation')}
      </Typography>
      {showError && (
        <Paper
          sx={{
            p: 2,
            mt: 2,
            bgcolor: '#ffdad6',
          }}
        >
          <Box display="flex" gap={2} alignItems="center" color="#c83727">
            <WarningIcon
              sx={{
                width: 32,
                height: 32,
                color: '#c83727',
              }}
            />

            <Box>
              <Typography fontWeight={600}>{translate('list.notice')}</Typography>

              <Typography variant="body2">{translate(`dialogs.${showError}`)}</Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </DialogContent>
    <DialogActions>
      <Button
        variant="outlined"
        color="inherit"
        onClick={handleClose}
        disabled={isDeleting}
        data-name="confirm-dialog-deny-button"
      >
        {translate('list.cancel')}
      </Button>

      <Button
        variant="contained"
        onClick={() => deleteOrganization()}
        disabled={isDeleting}
        data-name="confirm-dialog-confirm-button"
      >
        {translate('list.confirm')}
      </Button>
    </DialogActions>
  </Dialog>
);
